import React, { useEffect, useRef, useState } from "react";
import { PhoneOff, Pause, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { codexApi, downloadBlob } from "../lib/api";

/**
 * CallModal — silent voice flow built on MediaRecorder + OpenAI Whisper.
 *
 * Why not the Web Speech API?
 *   Browsers fire an OS-level chime every time SpeechRecognition starts and
 *   stops. There is no JS hook to suppress it, and in a phone-call style UI
 *   that beep fires constantly. Recording the mic ourselves with
 *   `MediaRecorder` is silent end-to-end and gives us the full audio buffer.
 *
 * Flow:
 *   1. `getUserMedia` → MediaStream → AudioContext analyser node feeds a live
 *      RMS volume value into the visual VU bar.
 *   2. MediaRecorder captures `audio/webm` chunks for the current turn.
 *   3. RMS-based silence detection: once we have heard at least one frame of
 *      speech (above THRESHOLD), any continuous run of `SILENCE_MS` below
 *      threshold ends the recording, ships it to /api/transcribe, and
 *      submits the resulting text as the user's question.
 *   4. While the OS is thinking + speaking, the mic is paused. When TTS
 *      finishes (or the user taps Interrupt), recording resumes for the
 *      next turn.
 */
export default function CallModal({ open, onClose, sessionId }) {
    const [turns, setTurns] = useState([]); // {role:'you'|'os', text}
    const [status, setStatus] = useState("idle"); // idle | listening | thinking | speaking
    const [muted, setMuted] = useState(false);
    const [studyBusy, setStudyBusy] = useState(false);
    const [level, setLevel] = useState(0); // 0..1 RMS for the VU bar
    const [silenceProgress, setSilenceProgress] = useState(0); // 0..1 countdown
    const [error, setError] = useState("");

    // Refs that survive re-renders
    const streamRef = useRef(null);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const dataArrayRef = useRef(null);
    const rafRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const silenceStartRef = useRef(null);
    const heardSpeechRef = useRef(false);
    const askInFlightRef = useRef(false);
    const audioRef = useRef(null);
    const turnsEndRef = useRef(null);
    const mutedRef = useRef(false);
    const openRef = useRef(false);
    const statusRef = useRef("idle");
    // Lets `interrupt()` resolve a pending `speak()` promise immediately so
    // the natural recorder restart in handleUtterance's finally block fires.
    const speakResolveRef = useRef(null);

    // Tuning knobs
    const VOLUME_THRESHOLD = 0.045; // RMS above this counts as speech
    const SILENCE_MS = 1500;        // pause this long after talking → auto-send (tightened from 2500ms)
    const MIN_SPEECH_MS = 400;      // ignore micro-blips shorter than this

    // Keep statusRef in sync so the rAF loop has fresh state without
    // re-binding the callback.
    useEffect(() => {
        statusRef.current = status;
    }, [status]);

    // ----------------------------------------------------------------- open

    useEffect(() => {
        openRef.current = open;
        if (!open) return;
        setTurns([]);
        setStatus("listening");
        setMuted(false);
        mutedRef.current = false;
        setError("");
        setLevel(0);
        startMic();
        return () => {
            cleanup();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        turnsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [turns]);

    // --------------------------------------------------------- mic plumbing

    async function startMic() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            streamRef.current = stream;
            // Volume analyser
            const Ctx = window.AudioContext || window.webkitAudioContext;
            const ctx = new Ctx();
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 1024;
            source.connect(analyser);
            audioCtxRef.current = ctx;
            analyserRef.current = analyser;
            dataArrayRef.current = new Uint8Array(analyser.fftSize);
            // Start recorder + visualiser
            beginRecording();
            tickAudio();
        } catch (e) {
            console.error("getUserMedia failed", e);
            setError(
                "Microphone access is required for voice calls. Please grant permission and try again."
            );
        }
    }

    function beginRecording() {
        if (!streamRef.current) return;
        // Guard: if a previous recorder is still active (e.g. after an
        // interrupt), stop it cleanly and detach its handlers before we
        // start a fresh one. Overlapping recorders would either double-
        // consume the stream or leak until GC and stall silence
        // detection on the new recorder.
        const prev = recorderRef.current;
        if (prev) {
            try {
                prev.ondataavailable = null;
                prev.onstop = null;
                if (prev.state !== "inactive") prev.stop();
            } catch (_e) {
                // ignore
            }
            recorderRef.current = null;
        }
        chunksRef.current = [];
        silenceStartRef.current = null;
        heardSpeechRef.current = false;
        setSilenceProgress(0);
        const mime = pickMime();
        let recorder;
        try {
            recorder = mime
                ? new MediaRecorder(streamRef.current, { mimeType: mime })
                : new MediaRecorder(streamRef.current);
        } catch (e) {
            console.error("MediaRecorder construct failed", e);
            return;
        }
        recorder.ondataavailable = (ev) => {
            if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
        };
        recorder.onstop = () => {
            const blob = new Blob(chunksRef.current, {
                type: recorder.mimeType || "audio/webm",
            });
            chunksRef.current = [];
            if (
                !heardSpeechRef.current ||
                blob.size < 2000 ||
                askInFlightRef.current ||
                !openRef.current
            ) {
                // Nothing meaningful was captured — silently restart
                // listening. Use statusRef (live) not the stale `status`
                // closure, so the guard fires correctly after an interrupt.
                if (openRef.current && statusRef.current !== "speaking") {
                    beginRecording();
                }
                return;
            }
            handleUtterance(blob, recorder.mimeType || "audio/webm");
        };
        recorderRef.current = recorder;
        recorder.start(250);
        setStatus("listening");
    }

    function pickMime() {
        if (typeof MediaRecorder === "undefined") return null;
        const candidates = [
            "audio/webm;codecs=opus",
            "audio/webm",
            "audio/ogg;codecs=opus",
            "audio/mp4",
        ];
        for (const c of candidates) {
            if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) {
                return c;
            }
        }
        return null;
    }

    function tickAudio() {
        const loop = () => {
            const analyser = analyserRef.current;
            const buf = dataArrayRef.current;
            if (!analyser || !buf) {
                rafRef.current = requestAnimationFrame(loop);
                return;
            }
            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) {
                const v = (buf[i] - 128) / 128;
                sum += v * v;
            }
            const rms = Math.sqrt(sum / buf.length);
            setLevel((prev) => prev * 0.6 + rms * 0.4);

            const isListening =
                openRef.current &&
                !mutedRef.current &&
                statusRef.current === "listening" &&
                recorderRef.current &&
                recorderRef.current.state === "recording";

            if (isListening) {
                const now = performance.now();
                if (rms > VOLUME_THRESHOLD) {
                    heardSpeechRef.current = true;
                    silenceStartRef.current = null;
                    setSilenceProgress(0);
                } else if (heardSpeechRef.current) {
                    if (silenceStartRef.current == null) {
                        silenceStartRef.current = now;
                    }
                    const elapsed = now - silenceStartRef.current;
                    const pct = Math.min(1, elapsed / SILENCE_MS);
                    setSilenceProgress(pct);
                    if (elapsed >= SILENCE_MS) {
                        setSilenceProgress(0);
                        silenceStartRef.current = null;
                        try {
                            recorderRef.current.stop();
                        } catch (_e) {
                            // ignore
                        }
                    }
                } else {
                    setSilenceProgress(0);
                }
            } else {
                setSilenceProgress(0);
            }
            rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
    }

    async function handleUtterance(blob, mime) {
        if (askInFlightRef.current) return;
        askInFlightRef.current = true;
        setStatus("thinking");
        try {
            const ext = mime.includes("ogg")
                ? "ogg"
                : mime.includes("mp4")
                  ? "mp4"
                  : "webm";
            const data = await codexApi.transcribe(blob, `voice.${ext}`);
            const text = (data?.text || "").trim();
            if (!text) {
                askInFlightRef.current = false;
                if (openRef.current && !mutedRef.current) beginRecording();
                return;
            }
            setTurns((t) => [...t, { role: "you", text }]);
            const res = await codexApi.ask(text, sessionId, "chat", true);
            const reply = res?.answer || "";
            setTurns((t) => [...t, { role: "os", text: reply }]);
            if (!res.blocked && reply) {
                await speak(reply);
            }
        } catch (e) {
            console.error("Voice turn failed", e);
            setTurns((t) => [
                ...t,
                {
                    role: "os",
                    text: "The Codex OS could not resolve that route. Please try again.",
                },
            ]);
        } finally {
            askInFlightRef.current = false;
            if (openRef.current && !mutedRef.current) {
                setStatus("listening");
                beginRecording();
            }
        }
    }

    async function speak(text) {
        const trimmed = (text || "").slice(0, 3800);
        if (!trimmed) return;
        setStatus("speaking");
        try {
            const blob = await codexApi.tts(trimmed, "onyx");
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audioRef.current = audio;
            await new Promise((resolve) => {
                // Save a handle so `interrupt()` can resolve the promise
                // immediately instead of leaving the call hanging.
                speakResolveRef.current = () => {
                    URL.revokeObjectURL(url);
                    speakResolveRef.current = null;
                    resolve();
                };
                audio.onended = () => speakResolveRef.current?.();
                audio.onerror = () => speakResolveRef.current?.();
                audio.play().catch(() => speakResolveRef.current?.());
            });
        } catch (_e) {
            // ignore
        } finally {
            audioRef.current = null;
        }
    }

    function interrupt() {
        // Stop the OS's current speech AND fully reset the mic pipeline
        // so recording restarts cleanly. Prior implementation just paused
        // the audio and relied on the natural finally-block restart, but
        // any residual TTS audio that leaked into the mic (feedback) had
        // already flipped heardSpeechRef=true on the still-active
        // recorder, so the "new" recorder inherited stale state and
        // never fired silence detection. Explicitly reset the flags and
        // let the finally block spin up a fresh recorder from zero.
        if (audioRef.current) {
            try {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            } catch (_e) {
                // ignore
            }
        }
        heardSpeechRef.current = false;
        silenceStartRef.current = null;
        setSilenceProgress(0);
        speakResolveRef.current?.();
    }

    function stopRecorder() {
        const rec = recorderRef.current;
        if (rec && rec.state === "recording") {
            heardSpeechRef.current = false; // discard whatever was captured
            try {
                rec.stop();
            } catch (_e) {
                // ignore
            }
        }
    }

    function cleanup() {
        openRef.current = false;
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        stopRecorder();
        if (audioRef.current) {
            try {
                audioRef.current.pause();
            } catch (_e) {
                // ignore
            }
            audioRef.current = null;
        }
        if (audioCtxRef.current) {
            try {
                audioCtxRef.current.close();
            } catch (_e) {
                // ignore
            }
            audioCtxRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        recorderRef.current = null;
        analyserRef.current = null;
        dataArrayRef.current = null;
        chunksRef.current = [];
    }

    function handleEnd() {
        cleanup();
        onClose();
    }

    /**
     * Synthesize a phone-format study PDF from the running call.
     * Topic seed = the most recent user turn; the OS already remembers the
     * full conversation history server-side via `session_id`, so the resulting
     * study reflects the whole exchange.
     */
    async function handleGenerateStudy() {
        const lastUser = [...turns].reverse().find((t) => t.role === "you");
        const topic = lastUser?.text?.trim();
        if (!topic) {
            toast.error("Ask a question on the call first, then I can compose the study.");
            return;
        }
        setStudyBusy(true);
        const tId = toast.loading("Composing a study from this call…");
        try {
            const packet = await codexApi.study(topic, sessionId);
            const blob = await codexApi.studyPdf(packet);
            const safe = (packet.title || "codex_study")
                .replace(/[^a-z0-9_-]+/gi, "_")
                .toLowerCase();
            downloadBlob(blob, `${safe}.pdf`);
            toast.success("Study PDF downloaded.", { id: tId });
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not generate study PDF.",
                { id: tId }
            );
        } finally {
            setStudyBusy(false);
        }
    }

    if (!open) return null;

    return (
        <div
            data-testid="call-modal"
            className="fixed inset-0 z-[120] flex items-center justify-center px-4 sm:px-6 py-6"
            role="dialog"
            aria-modal="true"
            aria-label="Voice call with the Codex OS"
        >
            <div
                className="absolute inset-0 bg-[#040810]/85 backdrop-blur-xl"
                onClick={handleEnd}
                aria-hidden="true"
            />
            <div className="relative w-full max-w-2xl codex-card flex flex-col max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-[#D4AF37]/30 flex items-center justify-between gap-4">
                    <div>
                        <p className="font-mono-os text-[10px] tracking-[0.36em] uppercase text-[#D4AF37]">
                            Voice Call
                        </p>
                        <p className="font-serif-display text-[#F9E596] text-2xl leading-none mt-1">
                            The Codex OS
                        </p>
                    </div>
                    <CallStatusBadge status={status} muted={muted} />
                </div>

                {/* Transcript */}
                <div
                    className="flex-1 overflow-y-auto scroll-thin px-6 py-5 space-y-3 min-h-[120px]"
                    data-testid="call-transcript"
                >
                    {turns.length === 0 && (
                        <p className="text-center text-[#CBD5E1] text-sm py-6">
                            {error
                                ? error
                                : "Speak when you're ready. After about a second and a half of silence the OS will reply on its own."}
                        </p>
                    )}
                    {turns.map((t, i) => (
                        <CallBubble key={i} role={t.role} text={t.text} />
                    ))}
                    <div ref={turnsEndRef} />
                </div>

                {/* VU meter + countdown bar — silent visual feedback */}
                <div className="px-6 pb-4 space-y-2">
                    <VuMeter level={level} status={status} muted={muted} />
                    <CountdownBar
                        progress={silenceProgress}
                        active={status === "listening" && !muted && silenceProgress > 0}
                    />
                </div>

                {/* Controls */}
                <div className="px-6 py-4 border-t border-[#D4AF37]/30 flex items-center justify-center gap-3 bg-[#050B14]/40">
                    <button
                        data-testid="call-interrupt-btn"
                        onClick={interrupt}
                        disabled={status !== "speaking"}
                        className="codex-icon-button disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Interrupt the OS"
                        title="Interrupt"
                    >
                        <Pause size={18} strokeWidth={1.6} />
                    </button>
                    <button
                        data-testid="call-generate-study-btn"
                        onClick={handleGenerateStudy}
                        disabled={studyBusy || turns.length === 0}
                        className="codex-button !px-4 !py-3 !text-[10px] !text-[#F9E596] !border-[#D4AF37]/70 hover:!border-[#F9E596] disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Generate a study from this call"
                        title="Generate study from this call"
                    >
                        {studyBusy ? (
                            <Loader2 size={13} strokeWidth={1.6} className="animate-spin" />
                        ) : (
                            <FileDown size={13} strokeWidth={1.6} />
                        )}
                        {studyBusy ? "Composing…" : "Generate Study"}
                    </button>
                    <button
                        data-testid="call-end-btn"
                        onClick={handleEnd}
                        className="codex-button !px-5 !py-3 !text-[11px] !text-[#F9D5D5] !border-[#F43F5E]/60 hover:!border-[#F43F5E]"
                        aria-label="End call"
                        title="End call"
                    >
                        <PhoneOff size={15} strokeWidth={1.6} />
                        End Call
                    </button>
                </div>
            </div>
        </div>
    );
}

function CallStatusBadge({ status, muted }) {
    if (muted) {
        return (
            <span className="font-mono-os text-[10px] tracking-[0.28em] uppercase text-[#F43F5E]">
                Muted
            </span>
        );
    }
    if (status === "thinking") {
        return (
            <span className="font-mono-os text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] flex items-center gap-2">
                <Loader2 size={12} strokeWidth={1.6} className="animate-spin" /> Routing
            </span>
        );
    }
    if (status === "speaking") {
        return (
            <span className="font-mono-os text-[10px] tracking-[0.28em] uppercase text-[#F9E596]">
                Speaking
            </span>
        );
    }
    return (
        <span className="font-mono-os text-[10px] tracking-[0.28em] uppercase text-[#4ADE80]">
            Listening
        </span>
    );
}

function CallBubble({ role, text }) {
    const isYou = role === "you";
    return (
        <div className={`flex ${isYou ? "justify-end" : "justify-start"}`}>
            <div
                className={`max-w-[85%] rounded-lg px-4 py-3 text-[14px] leading-[1.55] whitespace-pre-wrap backdrop-blur-md border ${
                    isYou
                        ? "bg-[#0B1426]/65 border-[#D4AF37]/30 text-[#F1F5F9]"
                        : "bg-[#0A1220]/65 border-[#D4AF37]/40 text-[#F9E596]"
                }`}
            >
                {text}
            </div>
        </div>
    );
}

/**
 * Real-time VU meter — 24 vertical bars whose heights follow the smoothed
 * RMS amplitude of the live mic. This is the visual replacement for the
 * old browser beep: callers see they are being heard.
 */
/**
 * Thin countdown bar that fills during the post-speech silence window. When
 * full, the recorder ships the audio. Any new sound resets it to empty.
 */
function CountdownBar({ progress, active }) {
    const pct = Math.round((progress || 0) * 100);
    return (
        <div
            className="h-1.5 w-full rounded-full bg-[#050B14]/60 border border-[#D4AF37]/15 overflow-hidden"
            data-testid="call-countdown"
            aria-hidden="true"
        >
            <span
                className={`block h-full rounded-full transition-[width] duration-75 ease-out ${
                    active
                        ? "bg-gradient-to-r from-[#D4AF37] via-[#F9E596] to-[#D4AF37] shadow-[0_0_12px_rgba(249,229,150,0.55)]"
                        : "bg-[#D4AF37]/20"
                }`}
                style={{ width: `${pct}%` }}
            />
        </div>
    );
}

/**
 * Real-time VU meter — 24 vertical bars whose heights follow the smoothed
 * RMS amplitude of the live mic. This is the visual replacement for the
 * old browser beep: callers see they are being heard.
 */
function VuMeter({ level, status, muted }) {
    const bars = 24;
    // Spread the smoothed RMS across the bars with a centre-weighted curve so
    // the meter feels alive even with quiet speech.
    const scaled = Math.min(1, level * 6);
    const tone =
        muted
            ? "bg-[#F43F5E]/70"
            : status === "speaking"
              ? "bg-[#F9E596]/85"
              : status === "thinking"
                ? "bg-[#D4AF37]/70"
                : "bg-[#4ADE80]/80";
    return (
        <div
            className="flex items-end justify-center gap-[3px] h-12 px-2 rounded-md bg-[#050B14]/30 border border-[#D4AF37]/15"
            data-testid="call-vu-meter"
            aria-hidden="true"
        >
            {Array.from({ length: bars }).map((_, i) => {
                // Centre bars taller than edge bars when energy is present.
                const c = Math.abs(i - (bars - 1) / 2) / ((bars - 1) / 2); // 0 centre, 1 edge
                const weight = 1 - c * 0.55;
                const h = Math.max(6, Math.round(scaled * 100 * weight));
                return (
                    <span
                        key={i}
                        className={`${tone} w-[5px] rounded-sm transition-[height] duration-75 ease-out`}
                        style={{ height: `${h}%` }}
                    />
                );
            })}
        </div>
    );
}
